
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { Link } from 'react-router-dom';

const ServicesPage = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const services = [
    {
      id: 'business-consulting',
      label: 'Service 01',
      title: 'Business Consulting',
      description: 'Strategic guidance to help you scale your business, optimize operations, and achieve sustainable growth. I work with startups and SMEs to develop actionable strategies that drive results.',
      image: 'https://public.readdy.ai/ai/img_res/75e154f3bed0ce23fa765fc786af4a11.jpg',
      includes: [
        'Business strategy development',
        'Market analysis and competitive research',
        'Financial planning and budgeting',
        'Operations optimization',
        'Growth strategy implementation',
        'Performance tracking and KPIs',
      ],
      idealFor: ['Startups', 'SMEs', 'Entrepreneurs', 'Business Owners'],
      color: 'primary-sage',
    },
    {
      id: 'ecommerce-management',
      label: 'Service 02',
      title: 'E-commerce Management',
      description: 'Comprehensive e-commerce solutions from store setup to optimization. Whether you\'re starting fresh or scaling an existing store, I help you maximize conversions and revenue.',
      image: 'https://readdy.ai/api/search-image?query=e-commerce%20online%20store%20management%20dashboard%20on%20computer%20screen%20showing%20product%20inventory%20sales%20analytics%20and%20order%20management%2C%20modern%20digital%20workspace%20with%20shopping%20cart%20icons%20and%20growth%20charts%2C%20clean%20professional%20setup%20highlighting%20online%20retail%20business%20success&width=800&height=600&seq=service-ecommerce-1&orientation=landscape',
      includes: [
        'E-commerce platform setup and configuration',
        'Product listing optimization',
        'Inventory management systems',
        'Conversion rate optimization',
        'Marketing automation setup',
        'Customer experience enhancement',
      ],
      idealFor: ['Online Retailers', 'Product Sellers', 'Digital Entrepreneurs'],
      color: 'primary-gold',
    },
    {
      id: 'wellness-coaching',
      label: 'Service 03',
      title: 'Health & Wellbeing Coaching',
      description: 'Holistic wellness programs designed to help busy entrepreneurs achieve balance, improve health habits, and maintain the energy needed for sustained success.',
      image: 'https://readdy.ai/api/search-image?query=wellness%20and%20health%20coaching%20session%20with%20African%20coach%20guiding%20client%20through%20mindfulness%20and%20healthy%20lifestyle%20planning%2C%20peaceful%20bright%20space%20with%20plants%20and%20natural%20elements%2C%20yoga%20mat%20and%20wellness%20journal%20visible%2C%20serene%20atmosphere%20promoting%20work%20life%20balance%20and%20holistic%20health&width=800&height=600&seq=service-wellness-1&orientation=landscape',
      includes: [
        'Personalized wellness assessments',
        'Nutrition and lifestyle guidance',
        'Stress management strategies',
        'Work-life balance coaching',
        'Habit formation and accountability',
        'Energy and productivity optimization',
      ],
      idealFor: ['Busy Entrepreneurs', 'Professionals', 'Health Seekers'],
      color: 'primary-sage',
    },
  ];

  const faqs = [
    {
      question: 'How long does a typical consulting engagement last?',
      answer: 'Most engagements range from 3-6 months, but I offer flexible packages based on your needs. We can start with a single consultation or commit to ongoing support.',
    },
    {
      question: 'Do you work with clients internationally?',
      answer: 'Yes! I work with clients globally via video calls and online collaboration tools. Time zone differences are never an issue.',
    },
    {
      question: 'What makes your approach different?',
      answer: 'I combine business strategy with wellness coaching, ensuring you grow your business without sacrificing your health. My approach is personalized, data-driven, and focused on sustainable results.',
    },
    {
      question: 'Can I combine multiple services?',
      answer: 'Absolutely! Many clients benefit from combining business consulting with wellness coaching, or e-commerce management with strategic guidance. I offer customized packages.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero */}
        <section ref={heroRef} className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-serif font-bold text-5xl lg:text-6xl text-primary-charcoal mb-6">
                Services Designed for Your Success
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Tailored solutions for business growth, e-commerce excellence, and holistic wellbeing
              </p>
            </motion.div>
          </div>
        </section>

        {/* Service Sections */}
        {services.map((service, index) => (
          <ServiceSection key={service.id} service={service} index={index} />
        ))}

        {/* FAQ */}
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <h2 className="font-serif font-bold text-4xl text-primary-charcoal text-center mb-12">
              Common Questions
            </h2>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <FAQItem key={index} faq={faq} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 bg-gradient-to-br from-primary-sage to-primary-gold">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center text-white">
            <h2 className="font-serif font-bold text-4xl lg:text-5xl mb-6">
              Not Sure Which Service?
            </h2>
            <p className="text-xl mb-10">
              Let's discuss your needs and find the perfect solution together
            </p>
            
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-10 py-5 bg-white text-primary-charcoal hover:bg-primary-beige font-bold text-lg rounded-lg transition-all duration-300 shadow-xl hover:shadow-2xl cursor-pointer whitespace-nowrap"
            >
              Schedule Free Consultation
              <i className="ri-calendar-line ml-3 text-xl"></i>
            </Link>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 mt-10">
              <a href="mailto:titilayoajayi923@gmail.com" className="flex items-center space-x-2 text-white hover:text-white/80 transition-colors cursor-pointer">
                <i className="ri-mail-line text-xl"></i>
                <span>Email</span>
              </a>
              <a href="tel:+2349060814354" className="flex items-center space-x-2 text-white hover:text-white/80 transition-colors cursor-pointer">
                <i className="ri-phone-line text-xl"></i>
                <span>Phone</span>
              </a>
              <a href="https://wa.me/2349047390448" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-white hover:text-white/80 transition-colors cursor-pointer">
                <i className="ri-whatsapp-line text-xl"></i>
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

const ServiceSection = ({ service, index }: { service: any; index: number }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const isEven = index % 2 === 0;

  return (
    <section ref={ref} className={`py-24 ${index % 2 === 0 ? 'bg-white' : 'bg-primary-beige'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
          <motion.div
            initial={{ opacity: 0, x: isEven ? -50 : 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className={!isEven ? 'lg:order-2' : ''}
          >
            <div className="relative rounded-3xl overflow-hidden h-[500px] shadow-xl">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: isEven ? 50 : -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={`space-y-6 ${!isEven ? 'lg:order-1' : ''}`}
          >
            <div className={`inline-flex items-center space-x-2 bg-${service.color} px-4 py-2 rounded-full`}>
              <span className="text-sm font-semibold text-white">{service.label}</span>
            </div>

            <h2 className="font-serif font-bold text-4xl text-primary-charcoal">{service.title}</h2>
            
            <p className="text-gray-700 leading-relaxed text-lg">{service.description}</p>

            <div>
              <h3 className="font-semibold text-lg text-primary-charcoal mb-4">What's Included:</h3>
              <ul className="space-y-3">
                {service.includes.map((item: string, idx: number) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <i className="ri-checkbox-circle-fill text-primary-sage mt-1"></i>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg text-primary-charcoal mb-3">Ideal For:</h3>
              <div className="flex flex-wrap gap-2">
                {service.idealFor.map((tag: string, idx: number) => (
                  <span key={idx} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-700">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-primary-gold hover:bg-accent-warm-gold text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Book Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const FAQItem = ({ faq, index }: { faq: any; index: number }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="bg-primary-beige rounded-2xl overflow-hidden"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-white/50 transition-colors"
      >
        <h4 className="font-semibold text-lg text-primary-charcoal pr-4">{faq.question}</h4>
        <i className={`ri-${isOpen ? 'subtract' : 'add'}-line text-2xl text-primary-sage flex-shrink-0`}></i>
      </button>
      
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="px-6 pb-5"
        >
          <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ServicesPage;
